import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';

import { API_BASE_URL } from '../../config/api';

function RouteView({ id, onNavigate }) {
  const { token } = useAuth();
  
  const [route, setRoute] = useState(null);
  const [journeyName, setJourneyName] = useState('N/A');
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      try {
        const [rRes, fRes] = await Promise.all([
          fetch(`${API_BASE_URL}/routes/${id}`, { headers }),
          fetch(`${API_BASE_URL}/admin/facilities`, { headers })
        ]);

        if (!rRes.ok) {
          setError('Failed to fetch route details');
          setLoading(false);
          return;
        }

        const routeData = await rRes.json();
        setRoute(routeData);

        if (fRes.ok) {
          setFacilities(await fRes.json());
        }

        if (routeData.journey_id) {
          const jRes = await fetch(`${API_BASE_URL}/journeys/${routeData.journey_id}`, { headers });
          if (jRes.ok) {
            const jData = await jRes.json();
            setJourneyName(jData.name);
          }
        }
      } catch (e) {
        console.error('Error fetching route view details', e);
        setError('Network error. Failed to load route details.');
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [id, token]);

  if (loading) {
    return <div className="card"><p>Loading route details...</p></div>;
  }

  if (error || !route) {
    return (
      <div className="card">
        <div style={{ color: '#f44336', marginBottom: '20px' }}>{error || 'Route not found'}</div>
        <button className="btn-primary" onClick={() => onNavigate('routes')}>Back to Directory</button>
      </div>
    );
  }

  const sortedWaypoints = (route.waypoints || []).sort((a, b) => a.sequence_no - b.sequence_no);

  const getFacBadgeClass = (type) => {
    switch (type) {
      case 'medical': return 'badge-danger';
      case 'accommodation': return 'badge-info';
      case 'food': return 'badge-success';
      default: return 'badge-info';
    }
  };

  const getWpIcon = (type) => {
    switch (type) {
      case 'START': return '🏁';
      case 'END': return '🚩';
      case 'CHECKPOINT': return '🛡️';
      default: return '📍';
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      
      {/* Route Details Card */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '16px' }}>
          <div>
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#212121', marginBottom: '6px' }}>{route.name}</h2>
            <span className="badge-info" style={{ fontSize: '14px', padding: '6px 12px', background: '#FF5722', color: '#fff' }}>
              {route.route_type || 'TRADITIONAL'} ROUTE
            </span>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button className="btn-outline" onClick={() => onNavigate('routes')}>Back to Directory</button>
            <button className="btn-primary" onClick={() => onNavigate('route-edit')}>Edit Route / Points</button>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
          <div>
            <h4 style={{ color: '#FF5722', marginBottom: '12px', fontSize: '16px' }}>Route Details</h4>
            <p style={{ marginBottom: '8px' }}><strong>Journey Season:</strong> {journeyName}</p>
            <p style={{ marginBottom: '8px' }}><strong>Active Status:</strong> {route.active ? 'Active' : 'Inactive'}</p>
            <p style={{ marginBottom: '8px' }}><strong>Total Route Points:</strong> {sortedWaypoints.length}</p>
          </div>
          <div>
            <h4 style={{ color: '#FF5722', marginBottom: '12px', fontSize: '16px' }}>Description / Info</h4>
            <p style={{ lineHeight: '1.6', color: '#555' }}>
              {route.description || 'No description provided for this route.'}
            </p>
          </div>
        </div>
      </div>

      {/* Route Timeline Track */}
      <div className="card">
        <h3>Route Sequence Timeline & Facility Mapping</h3>
        <p style={{ color: '#666', marginBottom: '24px' }}>Interactive track showing chronological points along the pilgrimage route and associated service camps.</p>

        {sortedWaypoints.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '40px 0', color: '#888' }}>
            No waypoints defined for this route. Go to the Edit page to add points.
          </p>
        ) : (
          <div className="timeline-container" style={{ position: 'relative', paddingLeft: '40px', marginTop: '20px' }}>
            {/* Center line */}
            <div style={{ 
              position: 'absolute', 
              left: '17px', 
              top: '12px', 
              bottom: '12px', 
              width: '4px', 
              background: '#FFDCCF', 
              borderRadius: '2px' 
            }}></div>

            {sortedWaypoints.map((wp, index) => {
              const mappedFacs = facilities.filter(f => f.waypoint_id === wp.id);
              
              return (
                <div key={wp.id} style={{ 
                  position: 'relative', 
                  marginBottom: index === sortedWaypoints.length - 1 ? '0' : '40px',
                }}>
                  {/* Timeline point dot */}
                  <div style={{ 
                    position: 'absolute', 
                    left: '-38px', 
                    top: '0', 
                    width: '30px', 
                    height: '30px', 
                    borderRadius: '50%', 
                    background: wp.waypoint_type === 'START' || wp.waypoint_type === 'END' ? '#FF5722' : '#fff', 
                    border: '3px solid #FF5722',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 2,
                    fontSize: '14px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}>
                    {getWpIcon(wp.waypoint_type)}
                  </div>

                  {/* Waypoint details */}
                  <div style={{ background: '#fcfcfc', border: '1px solid #f0f0f0', borderRadius: '8px', padding: '16px 20px', boxShadow: '0 1px 3px rgba(0,0,0,0.02)' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '8px' }}>
                      <h4 style={{ margin: 0, fontSize: '18px', color: '#333' }}>
                        {wp.sequence_no}. {wp.waypoint_name || 'Unnamed Waypoint'}
                      </h4>
                      <span className="badge-info" style={{ textTransform: 'capitalize', fontSize: '12px' }}>
                        {wp.waypoint_type}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '20px', fontSize: '13px', color: '#666', margin: '8px 0' }}>
                      <span><strong>Coordinates:</strong> {wp.latitude.toFixed(5)}, {wp.longitude.toFixed(5)}</span>
                      <span><strong>Geo-fence Radius:</strong> {wp.radius_meters}m</span>
                    </div>

                    {wp.description && (
                      <p style={{ margin: '4px 0 12px 0', fontSize: '14px', color: '#555', fontStyle: 'italic' }}>
                        "{wp.description}"
                      </p>
                    )}

                    {/* Mapped facilities box */}
                    <div style={{ borderTop: '1px solid #eee', paddingTop: '12px', marginTop: '12px' }}>
                      <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#FF5722', display: 'block', marginBottom: '8px' }}>
                        Active Service Camps ({mappedFacs.length}):
                      </span>

                      {mappedFacs.length === 0 ? (
                        <span style={{ fontSize: '13px', color: '#888', fontStyle: 'italic' }}>No camps mapped to this point.</span>
                      ) : (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                          {mappedFacs.map(f => (
                            <div key={f.id} style={{ 
                              background: '#fff', 
                              border: '1px solid #e0e0e0', 
                              borderRadius: '6px', 
                              padding: '8px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              fontSize: '13px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                            }}>
                              <span style={{ fontWeight: 'bold' }}>{f.name}</span>
                              <span className={getFacBadgeClass(f.facility_type)} style={{ fontSize: '10px', padding: '2px 6px' }}>
                                {f.facility_type}
                              </span>
                              {f.contact_number && <span style={{ color: '#888', fontSize: '11px' }}>📞 {f.contact_number}</span>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

    </div>
  );
}

export default RouteView;
